/** @jsx jsx */
import { jsx, Styled } from "theme-ui"
import PropTypes from "prop-types"
import { graphql } from "gatsby"
// import { DateTime } from "luxon"
import Img from "gatsby-image"
// import { StaticMap, Marker as MapboxMarker } from "react-map-gl"
// import "mapbox-gl/dist/mapbox-gl.css"
import { Flex } from "rebass"

// import Date from "../components/atoms/Date"
import List from "../components/atoms/List"
// import Marker from "../components/atoms/Marker"
import PageWrap from "../components/atoms/PageWrap"
import GridRow from "../components/atoms/GridRow"
import AnimateParallax from "../components/atoms/AnimateParallax"
import SEO from "../components/molecules/SEO"
import ProjectThumbnail from "../components/molecules/ProjectThumbnail"
import BlockContent from "../components/molecules/BlockContent"
import { Blocks } from "../components/organisms/Project/sanityBlocks"

import { ReactComponent as PinIcon } from "../images/icons/pin.svg"
// import { ReactComponent as CalendarIcon } from "../images/icons/calendar.svg"

import { localize } from "../utils/i18n"
// import { mapboxApiKey } from "../../../config"
import Pin from "../components/atoms/Pin"
import AnimateInView from "../components/atoms/AnimateInView"

const serializers = {
  types: {
    block: Blocks,
  },
}

const Project = ({
  data: { project, moreProjects, sanityMisc },
  location: { pathname },
}) => {
  const { _rawContent, coverImage, title, address } = project
  const { _rawMoreProjects } = sanityMisc

  return (
    <PageWrap sx={{ overflow: "hidden" }}>
      <SEO
        title={title}
        node={project}
        article
        pathname={pathname}
        banner={coverImage.asset.fluid.src}
      />
      <div
        sx={{
          height: ["auto", "70vh"],
          mx: [
            theme => -theme.space.pageWrap.small,
            theme => -theme.space.pageWrap.large,
          ],
          overflow: "hidden",
        }}
      >
        <AnimateParallax
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
          }}
          strength={300}
          spring={{ mass: 0.00001 }}
        >
          <Img
            fluid={coverImage.asset.fluid}
            sx={{
              width: "100%",
            }}
          />
        </AnimateParallax>
      </div>
      <GridRow sx={{ mt: [6, 7] }}>
        <GridRow.Col
          sx={{
            gridColumn: ["1 / -1", "1 / -1", "4 / span 9"],
            gridRow: ["auto", "auto", 1],
          }}
        >
          <AnimateInView y="0">
            <h2
              sx={{
                mt: 0,
                variant: "styles.root.h3",
              }}
            >
              {title}
            </h2>
          </AnimateInView>
        </GridRow.Col>
        <Styled.root
          sx={{
            gridColumn: ["1 / -1"],
            gridRow: ["auto", "auto", 1],
          }}
        >
          <GridRow
            as="section"
            rowGap={[0, 0]}
            sx={{
              "> *": {
                gridColumn: ["1 / -1", "1 / -1", "4 / -4"],
              },
            }}
          >
            <BlockContent blocks={_rawContent} serializers={serializers} />
          </GridRow>
        </Styled.root>
      </GridRow>
      {address && (address.street || address.city) && (
        <GridRow>
          <GridRow.Col
            sx={{
              gridColumn: ["1 / -1", "13 / span 9"],
            }}
          >
            {/* {location && (
            <AnimateInView y="0">
              <StaticMap
                width="100%"
                height={300}
                latitude={location.lat}
                longitude={location.lng}
                zoom={13}
                mapboxApiAccessToken={mapboxApiKey}
              >
                <MapboxMarker latitude={location.lat} longitude={location.lng}>
                  <Marker />
                </MapboxMarker>
              </StaticMap>
            </AnimateInView>
          )} */}
            <AnimateInView y="0">
              <Flex
                sx={{
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mt: 4,
                  mb: 6,
                }}
              >
                {address && address.street && address.city && (
                  <div
                    sx={{
                      maxWidth: "156px",
                      fontSize: 1,
                      fontWeight: "bold",
                      textTransform: "uppercase",
                    }}
                  >
                    <div>{address.street}</div>
                    <div>{address.city}</div>
                  </div>
                )}
                <List horizontal style={{ gridGap: 2 }}>
                  {address && address.city && (
                    <li>
                      <Pin icon={<PinIcon width={8} height={10} />}>
                        {address.city}
                      </Pin>
                    </li>
                  )}
                  {/* <li>
                  <Pin icon={<CalendarIcon width={11} height={10} />}>
                    <Date value={date} format={DateTime.DATE_SHORT} />
                  </Pin>
                </li> */}
                </List>
              </Flex>
            </AnimateInView>
          </GridRow.Col>
        </GridRow>
      )}
      <div
        sx={{
          position: "relative",
          pt: [4, 8],
          pb: 7,
          "::after": {
            content: '""',
            position: "absolute",
            zIndex: -1,
            top: 0,
            left: "50%",
            width: "100vw",
            height: "100%",
            transform: "translateX(-50%)",
            background: "linear-gradient(to bottom, #ffffff 0%,#f0f4ff 100%)",
          },
        }}
      >
        <h3
          sx={{ fontSize: [6, 8], fontWeight: "heading", textAlign: "center" }}
        >
          {_rawMoreProjects}
        </h3>
        <GridRow
          sx={{
            gridAutoFlow: "column",
            gridAutoColumns: "1fr",
          }}
        >
          {moreProjects.map((project, index) => (
            <GridRow.Col
              key={project.slug.current}
              sx={{
                mb: [5, 0],
                gridColumn:
                  index % 2
                    ? ["1 / -1", "13 / span 9"]
                    : ["1 / -1", "4 / span 9"],
              }}
            >
              <ProjectThumbnail project={project} />
            </GridRow.Col>
          ))}
        </GridRow>
      </div>
    </PageWrap>
  )
}

export default localize(Project)

Project.propTypes = {
  data: PropTypes.shape({
    project: PropTypes.object.isRequired,
    moreProjects: PropTypes.object.isRequired,
  }).isRequired,
}

// The typenames come from the slice names
// If this doesn't work for you query for __typename in body {} and GraphiQL will show them to you

export const pageQuery = graphql`
  query ProjectBySlug($slug: String!) {
    project: sanityProject(slug: { current: { eq: $slug } }) {
      _createdAt
      _updatedAt
      slug {
        current
      }
      title {
        ...StringAllLanguages
      }
      coverImage {
        asset {
          fluid(maxWidth: 1280) {
            ...GatsbySanityImageFluid
          }
        }
      }
      _rawContent(resolveReferences: { maxDepth: 5 })
      address {
        street
        city
      }
      date
      location {
        lat
        lng
      }
    }
    moreProjects: moreProjects(limit: 2) {
      ...ProjectThumbnail
    }
    sanityMisc {
      _rawMoreProjects(resolveReferences: { maxDepth: 5 })
    }
  }
`
